import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { and, eq, sql } from "@-/com.npmjs.drizzle-orm";
import {
  morphemeCorpusFilterFromCacheKey,
  wordCorpusSqlCondition,
} from "@-/dev.improve.haniverse.lib.morpheme.corpus";
import { useMorphemeCorpusFilter } from "@-/dev.improve.haniverse.lib.morpheme.corpus.use";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { hasLength } from "@-/com.npmjs.ts-array-length";

/**
 * A word's `word_definition` rows, folded: its reading (the first row's pinyin, in tone-number
 * form) and every definition in order. Both come from the same rows, so a word has both or neither.
 */
export type DictionaryWordEntry = {
  definitions: readonly [string, ...string[]];
  pinyin: string;
};

export type DictionaryWord = {
  /**
   * `null` when the word has no `word_definition` row — about 29% of linked words, including
   * common ones like 很多 — so it has no reading or definitions to show.
   */
  entry: DictionaryWordEntry | null;
  hsk2010Level: number | null;
  hsk2021Level: number | null;
  hsk2026Level: number | null;
  id: number;
  rank: number | null;
  tbclLevel: number | null;
  word: string;
};

/**
 * The most common words shown on a card's dictionary section is capped here. Only words inside the
 * user's word corpus count toward it (filtered in SQL, so the cap keeps in-corpus words); a word is
 * never dropped because of its characters.
 */
export const DICTIONARY_WORDS_LIMIT = 20;

export function useGetDictionaryWordsQueryKey() {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const { cacheKey: corpusCacheKey } = useMorphemeCorpusFilter();
  return (graphemeId: number) =>
    ["dictionaryWords", graphemeId, corpusCacheKey, { staticDb }] as const;
}

export const [useDictionaryWordsQueryOptions, dictionaryWordsQueryFn] = createUseQueryOptions(
  useGetDictionaryWordsQueryKey,
  async ({
    queryKey: [_, graphemeId, corpusCacheKey, { staticDb }],
  }): Promise<DictionaryWord[]> => {
    // Drive the lookup off the `word_grapheme` junction table, keyed by grapheme id.
    // This resolves through `word_grapheme_grapheme_id_idx` instead of the old
    // `LIKE '%glyph%'` substring match, which forced a full table scan of `word`.
    const words = await staticDb(import.meta.location)
      .select({
        hsk2010Level: staticSchema.wordPrcData.hsk2010Level,
        hsk2021Level: staticSchema.wordPrcData.hsk2021Level,
        hsk2026Level: staticSchema.wordPrcData.hsk2026Level,
        id: staticSchema.word.id,
        rank: staticSchema.wordSubtlex.rank,
        tbclLevel: staticSchema.wordTwData.tbclLevel,
        word: staticSchema.word.word,
      })
      .from(staticSchema.wordGrapheme)
      .innerJoin(staticSchema.word, eq(staticSchema.word.id, staticSchema.wordGrapheme.wordId))
      .leftJoin(staticSchema.wordSubtlex, eq(staticSchema.word.id, staticSchema.wordSubtlex.wordId))
      .leftJoin(staticSchema.wordPrcData, eq(staticSchema.word.id, staticSchema.wordPrcData.wordId))
      .leftJoin(staticSchema.wordTwData, eq(staticSchema.word.id, staticSchema.wordTwData.wordId))
      .where(
        and(
          eq(staticSchema.wordGrapheme.graphemeId, graphemeId),
          wordCorpusSqlCondition(morphemeCorpusFilterFromCacheKey(corpusCacheKey)),
        ),
      )
      // The character's own one-character word (the only one-character word containing it) comes
      // first so the limit never drops it — the info panel pins it atop its words. The rest sort by
      // frequency rank, keeping unranked words last. Without the explicit NULLS-LAST guard, the
      // left join's NULL ranks sort first in SQLite and crowd out common words (e.g. 一个) past
      // the limit.
      .orderBy(
        sql`length(${staticSchema.word.word}) != 1`,
        sql`${staticSchema.wordSubtlex.rank} is null`,
        staticSchema.wordSubtlex.rank,
      )
      .limit(DICTIONARY_WORDS_LIMIT);

    if (hasLength(words, 0)) {
      return [];
    }

    const wordIds = words.map((w) => w.id);
    const definitions = await staticDb(import.meta.location).query.wordDefinition.findMany({
      columns: { definition: true, pinyin: true, wordId: true },
      orderBy: { index: "asc" },
      where: { wordId: { in: wordIds } },
    });

    // Rows arrive in `index` order, so a word's first row carries its reading.
    const entryByWordId = new Map<number, { definitions: [string, ...string[]]; pinyin: string }>();
    for (const def of definitions) {
      const entry = entryByWordId.get(def.wordId);
      if (entry === undefined) {
        entryByWordId.set(def.wordId, { definitions: [def.definition], pinyin: def.pinyin });
      } else {
        entry.definitions.push(def.definition);
      }
    }

    return words.map((w) => ({
      entry: entryByWordId.get(w.id) ?? null,
      hsk2010Level: w.hsk2010Level,
      hsk2021Level: w.hsk2021Level,
      hsk2026Level: w.hsk2026Level,
      id: w.id,
      rank: w.rank,
      tbclLevel: w.tbclLevel,
      word: w.word,
    }));
  },
);
